import { useCallback, useEffect, useState } from 'react';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useProjectBaselineQuery } from 'queries/project-baseline-mutation';

interface Props {
  projectId: number;
}

export const useBaselineSelection = ({ projectId }: Props) => {
  const [initialised, setInitialised] = useState(false);

  const [cachedSelection, setCachedSelection] = useLocalStorage(
    `project.${projectId}.baseline.selection`,
    '',
  );

  const [selection, setSelection] = useState<number[]>([]);
  const [initialIndices, setInitialIndices] = useState<number[]>([]);

  const { data: baselines } = useProjectBaselineQuery(projectId);

  useEffect(() => {
    if (initialised || !baselines) {
      return;
    }
    setInitialised(true);
    if (!cachedSelection) {
      return;
    }
    const selectedIds = JSON.parse(cachedSelection);
    const validSelections: number[] = [];
    const indices: number[] = [];
    let isValidCache = true;

    selectedIds.forEach((id) => {
      const index = baselines.findIndex(
        (_baseline) => _baseline.baselineId === id,
      );

      if (index === -1) {
        isValidCache = false;

        return;
      }
      indices.push(index);
      validSelections.push(id);
    });

    setSelection(validSelections);
    if (!isValidCache) {
      setCachedSelection(JSON.stringify(validSelections));
    }
    setInitialIndices(indices);
  }, [baselines, cachedSelection, initialised, setCachedSelection]);

  useEffect(() => {
    if (!initialised) {
      return;
    }
    setCachedSelection(JSON.stringify(selection));
  }, [initialised, selection, setCachedSelection]);

  const set = useCallback((id: number) => {
    setSelection((_selection) => [..._selection, id]);
  }, []);

  const unset = useCallback((id: number) => {
    setSelection((_selection) => [..._selection.filter((_id) => _id !== id)]);
  }, []);

  return { selection, initialIndices, set, unset };
};
